import { useQuery } from 'react-query';
import axios from 'axios';
import type { ConexoesLinkDTO, ConexoesRespostaDTO } from '@webip/shared/src/dto/conexoes';

/** Intervalo de atualização enquanto a tela está "Executando". */
export const INTERVALO_MS = 10_000;

/** A partir desta fração da velocidade contratada o link é destacado. */
export const LIMIAR_LINK_CHEIO = 0.8;

/**
 * Conexões ao vivo da appliance. Mantém o último resultado enquanto busca o
 * próximo, para a tabela não piscar a cada atualização.
 */
export function useConexoes(applianceId: string | null, executando: boolean) {
    return useQuery(
        ['conexoes', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: ConexoesRespostaDTO }>(
                `/api/appliance/${applianceId}/conexoes`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            enabled: !!applianceId,
            retry: false,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            refetchInterval: executando ? INTERVALO_MS : false,
        },
    );
}

/**
 * Cor do link: segue a WAN (posição dela na lista ordenada por prioridade),
 * nunca o ranking de tráfego — senão a cor trocaria a cada atualização.
 * Variáveis --link-N definidas em conexoes.module.css (claro e escuro).
 */
export function corDoLink(links: ConexoesLinkDTO[] | undefined, wanId: number | null | undefined): string {
    if (wanId == null || !links) return 'var(--link-sem)';
    const idx = links.findIndex((l) => l.wan_id === wanId);
    return idx < 0 ? 'var(--link-sem)' : `var(--link-${(idx % 8) + 1})`;
}

export function nomeDoLink(links: ConexoesLinkDTO[] | undefined, wanId: number | null | undefined): string {
    if (wanId == null) return '';
    return links?.find((l) => l.wan_id === wanId)?.nome ?? `WAN ${wanId}`;
}

/** Taxa em bits/s, base 1000 (como as operadoras vendem o link). */
export function formatarTaxa(bps: number): string {
    if (bps >= 1e9) return `${(bps / 1e9).toFixed(2)} Gb/s`;
    if (bps >= 1e6) return `${(bps / 1e6).toFixed(2)} Mb/s`;
    if (bps >= 1e3) return `${(bps / 1e3).toFixed(2)} kb/s`;
    return `${Math.round(bps)} b/s`;
}

export function formatarBytes(bytes: number): string {
    if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
    if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${bytes} B`;
}

/** Ordena por uma chave; textos com `localeCompare` numérico (IPs ficam em ordem). */
export function ordenar<T>(linhas: T[], chave: (l: T) => string | number, desc: boolean): T[] {
    const out = [...linhas].sort((a, b) => {
        const va = chave(a);
        const vb = chave(b);
        if (typeof va === 'number' && typeof vb === 'number') return va - vb;
        return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
    });
    return desc ? out.reverse() : out;
}
